import { createPinia } from 'pinia'
import { createSSRApp, defineComponent, onMounted, ref } from 'vue'
import { createMemoryHistory, createRouter, createWebHistory } from 'vue-router'
import * as Sentry from '@sentry/vue'

import { createHead } from '@unhead/vue'
import { shallowEqual } from 'fast-equals'

import { clientPinia } from '/@shared/pinia'
import { getCookieFromString } from '/@shared/utils'
import { bus } from '/@shared/app'

import { routes } from './router'
import { type AppLocale, i18n, SUPPORT_LOCALES, selectLanguage } from './i18n'
import App from './App.vue'

function checkIsAsset(pathname: string) {
  let path = pathname
  if (path[path.length - 1] === '/') path = path.slice(0, -1)

  const tokens = path.split('.')
  const ext = tokens[tokens.length - 1]

  return [
    'js',
    'json',
    'css',
    'webp',
    'svg',
    'png',
    'ico',
    'jpg',
    'jpeg',
    'gif',
    'woff',
    'woff2',
    'map',
  ].includes(ext)
}

const ClientOnly = defineComponent({
  setup(_, { slots }) {
    const mounted = ref(false)
    onMounted(() => (mounted.value = true))

    return () => {
      if (!mounted.value) return slots.placeholder && slots.placeholder({})

      return slots.default && slots.default({})
    }
  },
})

function deserializeState(state: string) {
  try {
    return JSON.parse(state || '{}')
  } catch (error) {
    console.error('[SSR] On state deserialization -', error, state)
    return {}
  }
}

export function createApp() {
  /**
   * use createSSRApp to render the Vue App in the server
   * and send it to the user to do hydration process
   */
  const app = createSSRApp(App)

  const router = createRouter({
    history: import.meta.env.SSR ? createMemoryHistory() : createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
      if (to.hash) return { el: to.hash }

      if (!from.name) return {}

      const isDiffrentRoute = from.name !== to.name
      const isSameRouteButDifferentParams =
        from.name === to.name && !shallowEqual(from.params, to.params)

      if (isDiffrentRoute || isSameRouteButDifferentParams) return { top: 0 }

      if (savedPosition) return savedPosition
      return {}
    },
  })
  app.use(router)

  const head = createHead()
  app.use(head)

  app.component('ClientOnly', ClientOnly)

  if (import.meta.env.VITE_SENTRY_DSN && !import.meta.env.DEV) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.DEV ? 'dev' : 'production',
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /\.well-known\//i,
        /^chrome:\/\//i,
        /\/inj_js\//,
      ],
      sendDefaultPii: true,
      integrations: [Sentry.browserTracingIntegration({ router })],
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0,
    })
  }

  const context: {
    initialState: Record<string, any>
    ssrParams: Record<string, any>
  } = {
    initialState: {},
    ssrParams: {},
  }

  if (!import.meta.env.SSR) {
    // await documentReady()
    // @ts-expect-error global variable
    const globalState = window.__INITIAL_STATE__
    context.initialState = import.meta.env.SSR
      ? deserializeState(globalState)
      : globalState
  }

  /**
   * Create different pinia instance on every server call
   */
  const pinia = import.meta.env.SSR ? createPinia() : clientPinia
  app.use(pinia)

  app.use(i18n)

  if (import.meta.env.SSR) {
    // this will be stringified and set to window.__INITIAL_STATE__
    context.initialState.pinia = pinia.state.value
  } else {
    // set landing meta
    // @ts-expect-error global variable
    window.__FX_LANDING__ = {
      mode: import.meta.env.MODE,
      stateCopy:
        location.host === 'fornex.fun' ||
        location.host === 'stage.fornex.dev' ||
        import.meta.env.DEV
          ? JSON.parse(JSON.stringify(context.initialState?.pinia || {}))
          : undefined,
    }

    // on the client side, we restore the state
    pinia.state.value = context.initialState?.pinia || {}
  }

  let entryRoutePath: string | undefined
  let isFirstRoute = true

  router.beforeEach(async (to, _from, next) => {
    const cookieLang = import.meta.env.SSR
      ? undefined
      : getCookieFromString(document.cookie, 'django_language')

    if (
      to.name === 'LoginPage' ||
      to.name === 'SignupPage' ||
      to.name === 'PasswordResetPage' ||
      to.name === 'PasswordResetConfirmPage' ||
      to.name === 'ConfirmEmailPage'
    ) {
      const ssrDefaultLang = context.ssrParams.defaultLang
      const defaultLang =
        ssrDefaultLang || navigator.languages.find(x => x.includes('ru'))
          ? 'ru'
          : 'en'
      await selectLanguage((cookieLang || defaultLang || 'en') as AppLocale)
      next()
      return
    }

    const notFoundParams =
      (to.name === 'ErrorPage' && to.params.pathMatch) || undefined
    const mayBeLang = (
      'lang' in to.params ? to.params.lang : undefined
    ) as AppLocale

    let isFirstLangSet = true
    const isValidLang = SUPPORT_LOCALES.indexOf(mayBeLang) !== -1
    const isRogueAsset = checkIsAsset(to.path)

    if (isFirstLangSet && !isRogueAsset) {
      let defaultLang: AppLocale | undefined = 'en'

      if (to.name === 'ErrorPage') {
        const hasLangParam =
          SUPPORT_LOCALES.indexOf(notFoundParams?.[0] as AppLocale) !== -1

        if (cookieLang && !hasLangParam) defaultLang = cookieLang as AppLocale
        else if (hasLangParam) defaultLang = notFoundParams?.[0] as AppLocale
        else defaultLang = undefined
      }

      const lang: AppLocale | undefined = isValidLang ? mayBeLang : defaultLang
      if (lang) await selectLanguage(lang)
      isFirstLangSet = false
    }

    if (isFirstRoute || (entryRoutePath && entryRoutePath === to.path)) {
      // The first route is rendered in the server and its state is provided globally.
      isFirstRoute = false
      entryRoutePath = to.path
      to.meta.state = context.initialState
    }

    next()
  })

  if (!import.meta.env.SSR)
    bus.on('app::sentry::set-user', u => Sentry.setUser(u))

  return {
    app,
    router,
    head,
    ssrParams: context.ssrParams,
  }
}
